import { createSelector } from 'reselect';
import {
  CustomEvent,
  ListReferencesByEventName,
  State,
  WorkflowNodeReference,
  WorkflowReferencesByWorkflowId,
  WorkflowsByEventName,
} from './types';

// Parent selector
export const customEventsSelector = (state: State) => state.settings.customEvents;

/**
  The data required for the CustomEventsPage originates from a few endpoints.
  For sanity's sake, this is a quick overview:

  1. /usagemetrics/joinedEventData
    Custom events data from ElasticSearch, merged with workflow and list
    reference counts from Postgres.

  2. /settings/project.json
    Project-specific custom event permissions - whether any new events are
    allowed into the project, and event-specific allowed/saved permissions.


  3. /deletionPolicies/getForProject
    Deletion (a.k.a retention) policies for individual event types.
*/

/**
  1. /usagemetrics/joinedEventData
*/
type RefCountsByName = {
  [eventName: string]: number;
};
type EventsByName = {
  [eventName: string]: CustomEvent;
};

export const selectEvents = (state: State) => customEventsSelector(state)?.customEvents || [];

export const selectIsLoadingEvents = (state: State) => customEventsSelector(state)?.isLoading;

export const selectIsErrorEvents = (state: State) => customEventsSelector(state)?.isError;

const defaultEventsByName = {} as EventsByName;
export const selectEventsByName = (state: State) =>
  customEventsSelector(state)?.customEvents.reduce((acc: EventsByName, event) => {
    acc[event.eventName] = event;
    return acc;
  }, defaultEventsByName);

export const selectWorkflowReferenceCountsByName = createSelector(
  [selectEvents],
  (events: CustomEvent[]) =>
    events.reduce((acc: RefCountsByName, { eventName, workflowAndNodes }) => {
      const count = workflowAndNodes
        ? new Set(workflowAndNodes.map(({ workflowId }) => workflowId)).size
        : 0;
      acc[eventName] = count;
      return acc;
    }, {}),
);

const compileNodeReferencesByWorkflowId = (workflowAndNodes: WorkflowNodeReference[]) =>
  workflowAndNodes?.reduce(
    (acc: WorkflowReferencesByWorkflowId, workflow: WorkflowNodeReference) => {
      const { workflowName, workflowId, nodeId, nodeTitle } = workflow;
      // Create only one object per Workflow
      if (acc[workflowId]) {
        acc[workflowId] = {
          ...acc[workflowId],
          // Workflow id and name are shared, but workflow nodes are compiled
          workflowNodes: [...acc[workflowId].workflowNodes, { id: nodeId, title: nodeTitle }],
        };
      } else {
        acc[workflowId] = {
          workflowId,
          workflowName,
          workflowNodes: [{ id: nodeId, title: nodeTitle }],
        };
      }
      return acc;
    },
    {} as WorkflowReferencesByWorkflowId,
  );

export const selectWorkflowReferencesByName = createSelector(
  [selectEvents],
  (events: CustomEvent[]) =>
    events.reduce((acc: WorkflowsByEventName, { eventName, workflowAndNodes }) => {
      if (workflowAndNodes) {
        const workflowRef = compileNodeReferencesByWorkflowId(workflowAndNodes);
        acc[eventName] = workflowRef;
      }
      return acc;
    }, {} as WorkflowsByEventName),
);

export const selectListReferenceCountsByName = createSelector(
  [selectEvents],
  (events: CustomEvent[]) =>
    events.reduce((acc: RefCountsByName, { eventName, dynamicLists }) => {
      acc[eventName] = dynamicLists?.length || 0;
      return acc;
    }, {}),
);

export const selectListReferencesByName = createSelector([selectEvents], (events: CustomEvent[]) =>
  events.reduce((acc: ListReferencesByEventName, { eventName, dynamicLists }) => {
    if (dynamicLists) acc[eventName] = dynamicLists;
    return acc;
  }, {} as ListReferencesByEventName),
);

/**
  3. /deletionPolicies/getForProject
*/
export const selectIsLoadingDeletionPolicies = (state: State) =>
  customEventsSelector(state)?.isLoadingDeletionPolicies;

export const selectIsErrorDeletionPolicies = (state: State) =>
  customEventsSelector(state)?.isErrorDeletionPolicies;

export const selectRetentionPeriodsByName = (state: State) =>
  customEventsSelector(state)?.retentionPeriodsByEventName || {};
